import { useId, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

import { VoteStatus } from "types/voteEnum";
import groupAndSortElectionProposals from "../services/groupAndSortElectionProposals";

import DistributionCaseProposal from "components/vote/Votebox/common/Buttons/DistributionCaseProposal";
import ShowAllOptionsButton from "components/vote/Votebox/common/Buttons/ShowAllOptionsButton";
import ActionSection from "components/vote/Votebox/common/ActionSection";
import VoteGroup from "components/vote/Votebox/common/VoteGroup";
import { getRemainingWeight } from "components/vote/Votebox/common/services/getRemainingWeight";
import { getNbDecimal, roundDecimal } from "components/helper/numberHelper";
import { NumberOfVoicesText } from "./VoteDistribution.styles";

type Proposal = {
    disableOtherPropositionsIfSelected: boolean;
    id: string;
    text: string;
    isVisibleByDefault: boolean;
    image: string;
    description: string;
};

type Props = {
    propositions: Array<Proposal>;
    answers: Array<{
        id: string;
        weight: number;
    }>;
    isSecret: boolean;
    voterWeight: number;
    voteStatus: string;
    updateWeight: (propositionId: string, weight: number) => void;
    validateVote: () => void;
    resetVote: () => void;
    canAnswerBeUpdated: boolean;
    titleId: string;
    // Set when the user votes for several voters, to name each group after its voter.
    groupVoterName?: string;
};

const VoteDistribution = ({
    propositions,
    answers,
    isSecret,
    voterWeight,
    voteStatus,
    updateWeight,
    validateVote,
    resetVote,
    canAnswerBeUpdated,
    titleId,
    groupVoterName,
}: Props) => {
    const [shouldShowHiddenProposals, setShouldShowHiddenProposals] =
        useState(false);
    const hiddenProposalsId = useId();
    const hintId = useId();

    const { t } = useTranslation();

    const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

    const nbDecimal = getNbDecimal(voterWeight);
    const remainingWeight = getRemainingWeight(voterWeight, answers);

    const renderToggleHiddenProposalsButton = (hiddenCount: number) => {
        /* If one proposal is hidden, display the toggle */
        return hiddenCount > 0 ? (
            <ShowAllOptionsButton
                controlsId={hiddenProposalsId}
                handleToggle={() =>
                    setShouldShowHiddenProposals(!shouldShowHiddenProposals)
                }
                hiddenCount={hiddenCount}
                isExpanded={shouldShowHiddenProposals}
            />
        ) : null;
    };

    const { visibleProposals, hiddenProposals } = groupAndSortElectionProposals(
        propositions.map((prop) => {
            const isActive =
                VoteStatus.Success === voteStatus && isSecret
                    ? false
                    : answers.some(
                          (answer) =>
                              answer.id === prop.id && answer.weight > 0,
                      );

            return {
                ...prop,
                isActive,
            };
        }),
    );

    const renderProposals = (
        proposals: Array<Proposal & { isActive: boolean }>,
    ) => {
        const filteredInputRefs = inputRefs.current.filter(
            (ref) => ref !== null,
        );

        const focusNextInput = (currentInputId: string) => {
            const currentProposal = propositions.find(
                (proposal) => proposal.id === currentInputId,
            );

            if (!currentProposal) {
                return;
            }

            const indexOfCurrentProposal =
                propositions.indexOf(currentProposal);

            const nextInput = filteredInputRefs[
                indexOfCurrentProposal === filteredInputRefs.length - 1
                    ? 0
                    : indexOfCurrentProposal + 1
            ] as HTMLInputElement;

            nextInput?.focus();
        };

        return proposals.map((proposition) => {
            const handleBlur = (inputValue: number) => {
                const currentAnswerWeight = answers.find(
                    (answer) => answer.id === proposition.id,
                )?.weight as number;

                const maxValue: number = roundDecimal(
                    remainingWeight + currentAnswerWeight,
                    nbDecimal,
                );

                const newWeight =
                    inputValue >= maxValue ? maxValue : inputValue;

                updateWeight(proposition.id, newWeight);
            };

            const isDisabled = () => {
                if (VoteStatus.Success === voteStatus) {
                    return true;
                }

                if (proposition.isActive) {
                    return false;
                }

                if (0 === remainingWeight) {
                    return true;
                }

                return false;
            };

            return (
                <DistributionCaseProposal
                    key={proposition.id}
                    isActive={proposition.isActive}
                    text={proposition.text}
                    isDisabled={isDisabled()}
                    remainingWeight={remainingWeight}
                    handleBlur={handleBlur}
                    image={proposition.image}
                    description={proposition.description}
                    value={
                        answers.find((answer) => answer.id === proposition.id)
                            ?.weight
                    }
                    handleKeyUp={(e) => {
                        if ("Enter" === e.key) {
                            focusNextInput(proposition.id);
                        }
                    }}
                    currentRef={(el) =>
                        (inputRefs.current[Number(proposition.id)] = el)
                    }
                    nbDecimal={nbDecimal}
                    hintId={hintId}
                />
            );
        });
    };

    return (
        <>
            <NumberOfVoicesText>
                {t("numberOfVote")} {remainingWeight.toString()}/{voterWeight}
            </NumberOfVoicesText>
            <VoteGroup titleId={titleId} voterName={groupVoterName}>
                {renderProposals(visibleProposals)}
                {renderToggleHiddenProposalsButton(hiddenProposals.length)}
                <div id={hiddenProposalsId}>
                    {shouldShowHiddenProposals
                        ? renderProposals(hiddenProposals)
                        : null}
                </div>
            </VoteGroup>
            <span id={hintId} hidden>
                {t("distributionEnterHint")}
            </span>
            <ActionSection
                validateVote={validateVote}
                resetVote={resetVote}
                voteStatus={voteStatus as VoteStatus}
                remainingWeight={remainingWeight}
                canAnswerBeUpdated={canAnswerBeUpdated}
            />
        </>
    );
};

export default VoteDistribution;
